import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import {
  TABS_BY_LIFECYCLE_PHASE,
  TAB_SLUGS,
  parseProjectAddress,
  projectSlug,
  tabFromSlug,
  type LifecyclePhase,
  type WorkspaceTab,
} from '@scoutaround/script-breakdown-shared';

import { api } from '../lib/api.js';
import { WorkspaceShell } from '../components/WorkspaceShell.js';
import { HealthTag, LifecycleTag, PlanTag } from '../components/StatusTag.js';
import { Callout } from '../components/Callout.js';
import { ProjectPermissionsProvider } from '../lib/permissions.js';
import { ScriptAndScenesTab } from './tabs/ScriptAndScenesTab.js';
import { LocationsTab } from './tabs/LocationsTab.js';
import { ScheduleTab } from './tabs/ScheduleTab.js';
import { OverviewTab } from './tabs/OverviewTab.js';
import { TeamTab } from './tabs/TeamTab.js';
import { ActivityTab } from './tabs/ActivityTab.js';
import { FilesTab } from './tabs/FilesTab.js';
import { RequestsTab } from './tabs/RequestsTab.js';
import { ReferencesTab } from './tabs/ReferencesTab.js';
import { BillingTab } from './tabs/BillingTab.js';
import { ChatTab } from './tabs/ChatTab.js';
import { ConversationDrawer } from '../components/conversations/ConversationDrawer.js';
import { ComparePage } from './ComparePage.js';
import { TrackView } from '../components/TrackView.js';

export interface WorkspacePageProps {
  /**
   * The RAW URL segment, which is an address rather than an id.
   *
   * It may be a full slug (`northern-light-7fq2k9xd`), a bare key, or a bare
   * ULID from a link minted before readable addresses existed. Resolving it is
   * this component's job precisely because it already loads the project list to
   * render the shell — so the pretty URL costs no extra request.
   */
  readonly address: string;
  readonly tabSlug: string | undefined;
  readonly subSlug: string | undefined;
  readonly onNavigate: (path: string) => void;
  readonly onBack: () => void;
}

export function WorkspacePage({
  address,
  tabSlug,
  subSlug,
  onNavigate,
  onBack,
}: WorkspacePageProps) {
  const requestedTab = tabFromSlug(tabSlug);
  const location = useLocation();

  // Compare is a sub-view of Locations with its own address, so a producer can
  // send "compare these" rather than "open Locations then click compare".
  const compareRequirementId = requestedTab === 'LOCATIONS' ? (subSlug ?? null) : null;

  const projects = useQuery({ queryKey: ['projects'], queryFn: api.listProjects });

  // THE LIST IS ALSO THE AUTHORIZATION. It is already scoped to this user's
  // memberships, so an address belonging to somebody else's production resolves
  // to nothing and renders "not found" — the same answer a non-member gets from
  // the API, with no separate lookup to get wrong.
  const target = parseProjectAddress(address);
  const project = projects.data?.find((p) =>
    target === null ? false : 'urlKey' in target ? p.urlKey === target.urlKey : p.id === target.projectId,
  );

  // Everything below the shell speaks ULIDs; the address never reaches the API.
  const projectId = project?.id ?? '';

  const blocked = useQuery({
    queryKey: ['revisions-blocked', projectId],
    queryFn: () => api.revisionBlocked(projectId),
    // Until the address resolves there is no project to ask about, and asking
    // with a slug would 404 on every deep link.
    enabled: projectId !== '',
  });

  // Read on every tab, not just Billing: a producer discovers a production is
  // read-only by trying to change something, and the explanation has to be
  // wherever they were when it happened.
  const billing = useQuery({
    queryKey: ['billing', projectId],
    queryFn: () => api.billingState(projectId),
    enabled: projectId !== '',
  });

  // The same answer the permissions provider reads (same key), for the two platform
  // switches that decide whether there is a Conversations tab at all (ADR 0021).
  const membership = useQuery({
    queryKey: ['my-membership', projectId],
    queryFn: () => api.myMembership(projectId),
    enabled: projectId !== '',
    staleTime: 60_000,
    retry: false,
  });
  const conversationsOn = membership.data?.features?.conversations === true;
  const conversationSummary = useQuery({
    queryKey: ['conversation-summary', projectId],
    queryFn: () => api.conversationSummary(projectId),
    enabled: projectId !== '' && conversationsOn,
    refetchInterval: 30_000,
    staleTime: 10_000,
    retry: false,
  });

  if (!project) {
    return (
      <div style={{ padding: 40 }}>
        {/* A FAILED LIST IS NOT AN ABSENT PROJECT. The address resolves against
            the project list, so anything that stops the list arriving — a
            dropped network, a 500 — used to come out as "Project not found.",
            which is a statement about the producer's production and reads as
            "it is gone". The three answers are genuinely different and only one
            of them is about the project. */}
        {projects.isError ? (
          <Callout
            tone="danger"
            title="Could not load your productions"
            testId="workspace-list-error"
            action={
              <button
                type="button"
                className="ctl"
                data-testid="workspace-list-retry"
                onClick={() => {
                  void projects.refetch();
                }}
              >
                Try again
              </button>
            }
          >
            Nothing is lost — this is a problem reaching the server, not with
            your production. Try again in a moment.
          </Callout>
        ) : (
          <p className="mm">{projects.isPending ? 'Loading…' : 'Project not found.'}</p>
        )}
        <button
          type="button"
          className="ctl"
          onClick={onBack}
          style={{ marginTop: 12 }}
          data-testid="project-not-found-back-btn"
        >
          Back to projects
        </button>
      </div>
    );
  }

  const phase: LifecyclePhase = project.lifecyclePhase;

  // THE ADDRESS THIS PROJECT SHOULD BE AT. `urlKey` is nullable only in the
  // window between the column migration and the backfill reaching a row, so the
  // id is the fallback rather than a crash — a readable URL is a courtesy and
  // must never be the thing that makes a page unreachable.
  const canonical = project.urlKey === null ? project.id : projectSlug(project.title, project.urlKey);

  // THE TAB SET FOLLOWS THE PLAN AS WELL AS THE PHASE. Offering Schedule to a
  // production whose plan does not include the scheduling module is an
  // invitation to walk into a 403 — the tab is removed and Plan & billing says
  // what buys it back. Billing itself is never removed, for the same reason it
  // is in every phase: it is the way out.
  const features = billing.data?.features;
  const allowed = TABS_BY_LIFECYCLE_PHASE[phase].filter((t) => {
    if (features === undefined) return true;
    if (t === 'SCHEDULE') return features.scheduling;
    if (t === 'FILES') return features.mediaStorage;
    return true;
  }).filter((t) => {
    // Neither team conversations nor host messaging: nothing could ever be here.
    // Unknown (still loading) keeps the tab rather than flickering it away.
    if (t !== 'CHAT' || membership.data === undefined) return true;
    return membership.data.features?.conversations === true || membership.data.features?.hostMessages === true;
  });
  const unreadConversations = conversationSummary.data?.totals.unread ?? 0;

  // A TAB THIS PROJECT DOES NOT HAVE IS A REDIRECT, NOT AN ERROR. Bookmarks
  // outlive plans: someone saves the Schedule of a production, the plan that
  // included scheduling lapses, and the tab legitimately stops existing. Sending
  // them to Overview is right; a 404 on a link that worked last month is not.
  // (No phase removes a tab since 2026-10-29; the plan and the conversations flag
  // still do.)
  const tab: WorkspaceTab = requestedTab && allowed.includes(requestedTab) ? requestedTab : 'OVERVIEW';

  // AN OLD OR MISSPELT ADDRESS IS CORRECTED, NOT REFUSED. A link carrying a
  // bare ULID, or the name a production had before it was renamed, resolves
  // perfectly — the key is what identifies it — and the URL is then rewritten
  // to the canonical form so what somebody copies next is the readable one.
  // `replace`, so Back does not bounce between the two spellings. The query and
  // hash travel with it: an emailed link addresses the project by id and carries
  // its state there (`?discuss=…`, `?settings=email`), which the rewrite dropped.
  if (address !== canonical && tab === requestedTab) {
    return (
      <Navigate
        to={`/projects/${canonical}/${TAB_SLUGS[tab]}${subSlug === undefined ? '' : `/${subSlug}`}${location.search}${location.hash}`}
        replace
      />
    );
  }

  if (tab !== requestedTab) {
    return <Navigate to={`/projects/${canonical}/${TAB_SLUGS.OVERVIEW}`} replace />;
  }

  return (
    /**
     * MOUNTED PER PROJECT, and keyed by its id.
     *
     * What somebody may do is a fact about (person, project), never about the
     * person: one producer is routinely a location scout on the production next
     * door, under rules that production set. Mounting the provider here — inside
     * the branch that has already resolved WHICH project — is what stops one
     * production's answer being shown inside another.
     */
    <ProjectPermissionsProvider projectId={projectId}>
    <TrackView projectId={projectId} eventName="tab_viewed" props={{ tab }} />
    <ConversationDrawer projectId={projectId} />
    <WorkspaceShell
      phase={phase}
      tabs={allowed}
      activeTab={tab}
      badges={unreadConversations > 0 ? { CHAT: String(unreadConversations) } : {}}
      onTabChange={(next) => {
        onNavigate(`/projects/${canonical}/${TAB_SLUGS[next]}`);
      }}
      header={
        /* COMPACT (redesign of 2026-10-30): the way back, the name and where the
           production stands on two short lines, so every tab's own content starts
           higher. The page below never repeats any of it. */
        <div className="ws-project-head">
          <button type="button" className="ws-back" data-testid="back-to-projects" onClick={onBack}>
            ← Projects
          </button>
          <div className="ws-project-title">
            <h1 className="dh">{project.title}</h1>
            <div className="ws-project-tags">
              <LifecycleTag phase={phase} />
              <HealthTag derived={project.derivedHealth} override={project.healthOverride} />
              {billing.data ? (
                <PlanTag
                  state={billing.data.state}
                  planCode={billing.data.planCode}
                  termMonths={billing.data.termMonths}
                  periodStartsAt={billing.data.periodStartsAt}
                  periodEndsAt={billing.data.periodEndsAt}
                />
              ) : null}
            </div>
          </div>
        </div>
      }
    >
      {billing.data?.readOnly ? (
        <div style={{ marginBottom: 20 }}>
          <Callout
            tone="danger"
            title="This production is read-only"
            testId="read-only-banner"
          >
            The paid period has ended. Everything is still here and nothing will be deleted — open
            Plan &amp; billing to start editing again.
          </Callout>
        </div>
      ) : null}

      {blocked.data?.blocked ? (
        <div style={{ marginBottom: 20 }}>
          <Callout
            tone="danger"
            title="A script revision is waiting on you"
            testId="revision-blocker"
          >
            Matching is paused until the revision impacts are resolved. Results computed against
            scenes that have since changed would look exactly as trustworthy as correct ones.
          </Callout>
        </div>
      ) : null}

      {tab === 'OVERVIEW' ? (
        <OverviewTab
          projectId={projectId}
          address={canonical}
          format={project.format}
          derivedHealth={project.derivedHealth}
          healthOverride={project.healthOverride}
        />
      ) : null}
      {tab === 'SCRIPT_AND_SCENES' ? <ScriptAndScenesTab projectId={projectId} subSlug={subSlug} onNavigate={onNavigate} /> : null}
      {tab === 'LOCATIONS' ? (
        compareRequirementId ? (
          <ComparePage
            projectId={projectId}
            requirementId={compareRequirementId}
            onBack={() => {
              onNavigate(`/projects/${canonical}/${TAB_SLUGS.LOCATIONS}`);
            }}
          />
        ) : (
          <LocationsTab
            projectId={projectId}
            projectTitle={project.title}
            onCompare={(requirementId) => {
              onNavigate(`/projects/${canonical}/${TAB_SLUGS.LOCATIONS}/${requirementId}`);
            }}
          />
        )
      ) : null}
      {tab === 'SCHEDULE' ? <ScheduleTab projectId={projectId} subSlug={subSlug} onNavigate={onNavigate} /> : null}
      {tab === 'BILLING' ? <BillingTab projectId={projectId} /> : null}
      {tab === 'REFERENCES' ? <ReferencesTab projectId={projectId} /> : null}
      {tab === 'REQUESTS' ? <RequestsTab projectId={projectId} /> : null}
      {tab === 'FILES' ? <FilesTab projectId={projectId} /> : null}
      {tab === 'TEAM' ? <TeamTab projectId={projectId} /> : null}
      {tab === 'ACTIVITY' ? <ActivityTab projectId={projectId} /> : null}
      {tab === 'CHAT' ? (
        <ChatTab
          projectId={projectId}
          threadId={subSlug ?? null}
          onOpenThread={(id) => {
            onNavigate(`/projects/${canonical}/${TAB_SLUGS.CHAT}${id ? `/${id}` : ''}`);
          }}
        />
      ) : null}
    </WorkspaceShell>
    </ProjectPermissionsProvider>
  );
}
